import { forwardRef, useCallback } from 'react';
import { DraggableProvided, DraggableStateSnapshot } from '@hello-pangea/dnd';
import classNames from 'classnames';
import { OutgoingSectionDto } from 'api/dtos/events/products';
import { SectionHeader } from '../SectionHeader';
import AddTierToCategoryButton from './components/AddTierToCategoryButton';
import { useSectionsContext } from '../../context/SectionsContext';

interface SectionItemProps {
  section: OutgoingSectionDto;
  provided: DraggableProvided;
  snapshot: DraggableStateSnapshot;
  snapshotSections: { isDraggingOver: boolean };
  onNameBlur: (sectionId: string) => Promise<void>;
  onDeleteSection: (sectionId: string) => void;
  onCancelEdit: () => void;
  inputRef: React.RefObject<HTMLInputElement>;
}

export const SectionItem = forwardRef<HTMLDivElement, SectionItemProps>(
  (
    {
      section,
      provided,
      snapshot,
      snapshotSections,
      onNameBlur,
      onDeleteSection,
      onCancelEdit,
      inputRef
    },
    ref
  ) => {
    const {
      selectedSection,
      sectionNames,
      expandedSections,
      tiersBySection,
      tiers,
      toggleSection,
      handleNameChange: contextHandleNameChange,
      editSection,
      deleteMutation,
      addEventPricingTierToSection,
      setAddTierToSectionId,
      children
    } = useSectionsContext();

    const isEditing = selectedSection === section.id;
    const sectionTiers = tiersBySection[section.id] || [];
    const isExpanded = !expandedSections[section.id];
    const sectionName = sectionNames[section.id] ?? section.name;

    const handleToggleSection = useCallback(() => {
      toggleSection(section.id);
    }, [toggleSection, section.id]);

    const handleNameChange = useCallback((name: string) => {
      contextHandleNameChange(section.id, name);
    }, [contextHandleNameChange, section.id]);

    const handleNameBlur = useCallback(() => {
      return onNameBlur(section.id);
    }, [onNameBlur, section.id]);

    const handleDeleteSection = useCallback(() => {
      onDeleteSection(section.id);
    }, [onDeleteSection, section.id]);

    const handleEditSection = useCallback(() => {
      editSection(section.id, section.name);
    }, [editSection, section.id, section.name]);

    const handleAddTier = useCallback((sectionId: string) => {
      setAddTierToSectionId?.(sectionId);
    }, [setAddTierToSectionId]);

    return (
      <div
        ref={ref}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        className={classNames(
          'relative px-0 w-full border-2 border-transparent rounded-lg transition-colors',
          {
            'border-[#6585FF] shadow-lg': snapshot.isDragging
          }
        )}
      >
        <div className="w-full">
          <div className="flex items-center">
            <div
              className={classNames(
                'pl-2 md:pl-5 w-full ml-auto flex flex-col bg-white rounded-2xl transition-all p-0 duration-200 ease-in',
                {
                  'shadow-[-3px_14px_19.3px_0px_rgba(32,32,32,0.1)] border-2 border-[#6585FF]':
                    snapshot.isDragging,
                  '!mx-1':
                    !snapshot.draggingOver && snapshotSections.isDraggingOver
                }
              )}
            >
              <SectionHeader
                section={section}
                isEditing={isEditing}
                isExpanded={isExpanded}
                sectionName={sectionName}
                tierCount={sectionTiers.length}
                onToggleSection={handleToggleSection}
                onNameChange={handleNameChange}
                onNameBlur={handleNameBlur}
                onDeleteSection={handleDeleteSection}
                onEditSection={handleEditSection}
                onCancelEdit={onCancelEdit}
                deleteMutation={deleteMutation}
                inputRef={inputRef}
              />

              <div
                className={classNames('md:-ml-4 mr-4', {
                  '-mt-4 md:-mt-6': isExpanded
                })}
              >
                {isExpanded &&
                  children(
                    sectionTiers.sort((a, b) => a.sortIndex - b.sortIndex),
                    section.id
                  )}
                {addEventPricingTierToSection && isExpanded && (
                  <AddTierToCategoryButton
                    sectionId={section.id}
                    productsLength={tiers?.length || 0}
                    onAddTier={handleAddTier}
                  />
                )}
              </div>
            </div>
          </div>
          <div
            className={classNames(
              'border-b-2 border-[#EBEBEB] -mx-10 md:mx-2 transition-opacity',
              {
                'opacity-0': snapshot.isDragging || snapshotSections.isDraggingOver,
                'opacity-100': !snapshot.isDragging && !snapshotSections.isDraggingOver
              }
            )}
          />
        </div>
      </div>
    );
  }
);

SectionItem.displayName = 'SectionItem'; 